import React, { useState, useEffect } from 'react';
import { Button, Typography, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate, Link } from 'react-router-dom';
import { getAlphaSystemStatus } from '../../../api/alpha';
import './Pricing.css';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import Seo from '../../common/Seo';

const Pricing = () => {
  const navigate = useNavigate();
  const [alphaStatus, setAlphaStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlphaStatus = async () => {
      try {
        const status = await getAlphaSystemStatus();
        setAlphaStatus(status);
      } catch (error) {
        // If can't fetch, assume alpha is not active
        setAlphaStatus({ alphaEnabled: false, inviteRequired: false });
      } finally {
        setLoading(false);
      }
    };
    fetchAlphaStatus();
  }, []);

  const handleGetStarted = (planName) => {
    logEvent(analytics, 'pricing_plan_selected', { plan: planName });
    navigate('/signup');
  };

  const handleJoinWaitlist = () => {
    logEvent(analytics, 'pricing_waitlist_clicked', {});
    navigate('/waitlist');
  };

  const isInviteOnly = alphaStatus?.alphaEnabled && alphaStatus?.inviteRequired;

  if (loading) {
    return (
      <div className="ntb-pricing-container">
        <Seo
          title="Pricing - NanoTradingCopilot"
          description="Simple, transparent pricing for trade validation. Upgrade for AI-powered rules and unlimited daily validations."
          keywords="forex pricing, trading tools cost, trade validation price"
        />
        <div className="ntb-pricing-loading">
          <CircularProgress />
        </div>
      </div>
    );
  }

  // Show Alpha/Invite-Only page when alpha is enabled
  if (isInviteOnly) {
    return (
      <div className="ntb-pricing-container">
        <Seo
          title="Information - NanoTradingCopilot"
          description="NanoTradingCopilot is currently in private alpha. Join the waitlist for early access to our trade validation platform."
        />
        {/* Alpha Header */}
        <div className="ntb-pricing-header">
          <div className="ntb-alpha-badge">Private Alpha</div>
          <Typography variant="h1" className="ntb-pricing-title">
            Currently Invite-Only
          </Typography>
          <Typography variant="body1" className="ntb-pricing-subtitle">
            We're in private alpha, carefully onboarding traders to ensure the best experience.
            <br />Join the waitlist to get early access.
          </Typography>
        </div>

        {/* Alpha Benefits */}
        <div className="ntb-alpha-benefits">
          <div className="ntb-alpha-benefit-card">
            <LockIcon className="ntb-alpha-benefit-icon" />
            <Typography variant="h6">Exclusive Access</Typography>
            <Typography variant="body2">
              Limited spots available for founding members who will shape the product
            </Typography>
          </div>
          <div className="ntb-alpha-benefit-card">
            <GroupAddIcon className="ntb-alpha-benefit-icon" />
            <Typography variant="h6">Skip the Line</Typography>
            <Typography variant="body2">
              Refer friends to move up the waitlist and get access sooner
            </Typography>
          </div>
          <div className="ntb-alpha-benefit-card">
            <RocketLaunchIcon className="ntb-alpha-benefit-icon" />
            <Typography variant="h6">Free During Alpha</Typography>
            <Typography variant="body2">
              Alpha testers get full access at no cost while we're in development
            </Typography>
          </div>
        </div>

        {/* Waitlist CTA */}
        <div className="ntb-alpha-cta">
          <Button
            variant="contained"
            size="large"
            onClick={handleJoinWaitlist}
            className="ntb-alpha-cta-btn"
          >
            Join the Waitlist
          </Button>
          <Typography variant="body2" className="ntb-alpha-cta-note">
            Have an invite code? <Link to="/signup">Sign up here</Link>
          </Typography>
        </div>

        {/* What's Coming */}
        <div className="ntb-pricing-note">
          <Typography variant="h4" className="ntb-note-title">
            What Alpha Testers Get
          </Typography>
          <div className="ntb-note-grid">
            <div className="ntb-note-item">
              <Typography variant="subtitle2" className="ntb-note-label">Full Access</Typography>
              <Typography variant="body2" className="ntb-note-text">
                All features unlocked during alpha testing period
              </Typography>
            </div>
            <div className="ntb-note-item">
              <Typography variant="subtitle2" className="ntb-note-label">Direct Feedback</Typography>
              <Typography variant="body2" className="ntb-note-text">
                Shape the product with direct access to the development team
              </Typography>
            </div>
            <div className="ntb-note-item">
              <Typography variant="subtitle2" className="ntb-note-label">Founding Member Perks</Typography>
              <Typography variant="body2" className="ntb-note-text">
                Special pricing and benefits when we launch publicly
              </Typography>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="ntb-pricing-faq">
          <Typography variant="h4" className="ntb-faq-title">
            Questions
          </Typography>
          <div className="ntb-faq-grid">
            <div className="ntb-faq-item">
              <Typography variant="subtitle1" className="ntb-faq-q">
                How do I get an invite?
              </Typography>
              <Typography variant="body2" className="ntb-faq-a">
                Join the waitlist and you'll receive an invite code when a spot opens up.
                Refer friends to move up the queue faster.
              </Typography>
            </div>
            <div className="ntb-faq-item">
              <Typography variant="subtitle1" className="ntb-faq-q">
                Is alpha access really free?
              </Typography>
              <Typography variant="body2" className="ntb-faq-a">
                Yes! Alpha testers get full access at no cost. We value your feedback
                as we refine the product before public launch.
              </Typography>
            </div>
            <div className="ntb-faq-item">
              <Typography variant="subtitle1" className="ntb-faq-q">
                What happens after alpha?
              </Typography>
              <Typography variant="body2" className="ntb-faq-a">
                Alpha testers will receive special founding member pricing and early access
                to new features when we launch publicly.
              </Typography>
            </div>
            <div className="ntb-faq-item">
              <Typography variant="subtitle1" className="ntb-faq-q">
                How long will alpha last?
              </Typography>
              <Typography variant="body2" className="ntb-faq-a">
                We're focused on quality over speed. Alpha will continue until we're confident
                the product delivers real value to traders.
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Free-tier-only pricing page (paid tiers not yet available)
  return (
    <div className="ntb-pricing-container">
      <Seo
        title="Pricing - NanoTradingCopilot"
        description="Start validating trades for free. Paid plans with AI-powered rules coming soon."
        keywords="forex pricing, trading tools cost, trade validation price, free trading tool"
      />
      {/* Header */}
      <div className="ntb-pricing-header">
        <div className="ntb-early-bird-badge">Now Open</div>
        <Typography variant="h1" className="ntb-pricing-title">
          Start Validating Trades for Free
        </Typography>
        <Typography variant="body1" className="ntb-pricing-subtitle">
          Pre-trade validation with static rules. No credit card required.
        </Typography>
      </div>

      {/* Free Tier Card — Featured */}
      <div className="ntb-pricing-cards ntb-pricing-cards-single">
        <div className="ntb-pricing-card ntb-card-featured">
          <div className="ntb-recommended-badge">Get Started</div>
          <div className="ntb-card-header">
            <Typography variant="h3" className="ntb-plan-name">Free</Typography>
            <div className="ntb-plan-price">
              <span className="ntb-price-amount">$0</span>
              <span className="ntb-price-period">/month</span>
            </div>
            <Typography variant="body2" className="ntb-plan-desc">
              Everything you need to validate trades
            </Typography>
          </div>
          <ul className="ntb-plan-features">
            <li>
              <CheckIcon className="ntb-check" />
              <span>5 validations per day</span>
            </li>
            <li>
              <CheckIcon className="ntb-check" />
              <span>Up to 5 static rules per pipeline</span>
            </li>
            <li>
              <CheckIcon className="ntb-check" />
              <span>Major forex pairs</span>
            </li>
            <li>
              <CheckIcon className="ntb-check" />
              <span>Real-time market data</span>
            </li>
            <li>
              <CheckIcon className="ntb-check" />
              <span>Onboarding tour to get you started</span>
            </li>
          </ul>
          <Button
            variant="contained"
            className="ntb-plan-btn ntb-btn-primary"
            onClick={() => handleGetStarted('free')}
          >
            Sign Up Free
          </Button>
        </div>
      </div>

      {/* Paid Plans Coming Soon */}
      <div className="ntb-coming-soon">
        <Typography variant="h4" className="ntb-coming-soon-title">
          Paid Plans Coming Soon
        </Typography>
        <Typography variant="body1" className="ntb-coming-soon-subtitle">
          We're building AI-powered validation rules, expanded instrument coverage, and more.
          Paid tiers will unlock higher limits and advanced features.
        </Typography>
        <div className="ntb-coming-soon-tiers">
          <div className="ntb-coming-soon-tier">
            <Typography variant="h6" className="ntb-coming-soon-tier-name">Basic</Typography>
            <Typography variant="body2" className="ntb-coming-soon-tier-desc">
              More validations, AI rules, and email support
            </Typography>
          </div>
          <div className="ntb-coming-soon-tier">
            <Typography variant="h6" className="ntb-coming-soon-tier-name">Pro</Typography>
            <Typography variant="body2" className="ntb-coming-soon-tier-desc">
              Unlimited validations, all instruments, and priority support
            </Typography>
          </div>
        </div>
      </div>

      {/* What It Does / Doesn't Do */}
      <div className="ntb-pricing-clarity">
        <div className="ntb-clarity-section">
          <Typography variant="h5" className="ntb-clarity-title ntb-clarity-does">
            ✓ What this tool does
          </Typography>
          <ul className="ntb-clarity-list">
            <li>Validates trade setups before execution</li>
            <li>Enforces your risk and discipline rules</li>
            <li>Provides validation results with reasoning</li>
          </ul>
        </div>
        <div className="ntb-clarity-section">
          <Typography variant="h5" className="ntb-clarity-title ntb-clarity-doesnt">
            ✗ What this tool does NOT do
          </Typography>
          <ul className="ntb-clarity-list">
            <li>Does not place trades for you</li>
            <li>Does not generate trading signals</li>
            <li>Does not predict price movements</li>
            <li>Does not guarantee profits</li>
          </ul>
        </div>
      </div>

      {/* Early Access Note */}
      <div className="ntb-pricing-note">
        <Typography variant="h4" className="ntb-note-title">
          Why Start Now
        </Typography>
        <div className="ntb-note-grid">
          <div className="ntb-note-item">
            <Typography variant="subtitle2" className="ntb-note-label">Build Your Pipeline</Typography>
            <Typography variant="body2" className="ntb-note-text">
              Set up your validation rules now and refine them over time
            </Typography>
          </div>
          <div className="ntb-note-item">
            <Typography variant="subtitle2" className="ntb-note-label">Shape the Product</Typography>
            <Typography variant="body2" className="ntb-note-text">
              Early users get direct influence on features and priorities
            </Typography>
          </div>
          <div className="ntb-note-item">
            <Typography variant="subtitle2" className="ntb-note-label">Early Adopter Perks</Typography>
            <Typography variant="body2" className="ntb-note-text">
              Special pricing when paid plans launch for existing users
            </Typography>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="ntb-pricing-faq">
        <Typography variant="h4" className="ntb-faq-title">
          Questions
        </Typography>
        <div className="ntb-faq-grid">
          <div className="ntb-faq-item">
            <Typography variant="subtitle1" className="ntb-faq-q">
              What is a validation?
            </Typography>
            <Typography variant="body2" className="ntb-faq-a">
              Each time you run your trade through the validation pipeline counts as one validation.
            </Typography>
          </div>
          <div className="ntb-faq-item">
            <Typography variant="subtitle1" className="ntb-faq-q">
              Is the free tier really free?
            </Typography>
            <Typography variant="body2" className="ntb-faq-a">
              Yes. No credit card, no trial expiration. Use 5 validations per day with static rules for as long as you want.
            </Typography>
          </div>
          <div className="ntb-faq-item">
            <Typography variant="subtitle1" className="ntb-faq-q">
              When will paid plans be available?
            </Typography>
            <Typography variant="body2" className="ntb-faq-a">
              We're working on AI-powered rules and expanded features. Paid plans will launch when these are ready. Existing free users will get early adopter pricing.
            </Typography>
          </div>
          <div className="ntb-faq-item">
            <Typography variant="subtitle1" className="ntb-faq-q">
              Which instruments are available?
            </Typography>
            <Typography variant="body2" className="ntb-faq-a">
              Major forex pairs are fully supported on the free tier. More instruments coming with paid plans.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
